<template>
  <div ref="rootElement" class="flex flex-col gap-4">
    <!--
      Section header. Hidden via `hide-header` when the section is hosted inside
      the ANNOUNCEMENTS modal, whose own "Announcements" header already
      titles the surface (the Figma modal body is the thread list only — no
      "Threads" subheader). The default (false) keeps the inline channel-page
      treatment byte-for-byte unchanged. The create button is still permission-
      gated, so for a regular fan only the heading is suppressed.
    -->
    <div v-if="!hideHeader" class="flex justify-between items-center">
      <h2 class="text-xl text-text-brand font-serif uppercase">
        {{ $t("channel.sections.threads.title") }}
      </h2>
      <Button
        v-if="channelStore.permissions.manageThreads"
        type="button"
        variant="icon"
        class="btn-circle border-border-brand size-10"
        @click="onClick"
      >
        <Icon class="text-text size-6" icon="ic:baseline-plus"
      /></Button>
    </div>

    <div v-if="loading" class="flex flex-col gap-4">
      <ThreadCardSkeleton v-for="index in 3" :key="index" :index="index - 1" />
    </div>

    <!--
      Empty state. The default "heading" variant (large centered headline) is the
      legacy channel-layout treatment and is kept byte-for-byte. The "subtle"
      variant is used by the new-layout ANNOUNCEMENTS grouping, where the
      threads sit beneath the HOW-IT-WORKS lead card; a quiet line reads better
      there and matches the contests section's empty-state styling.
    -->
    <p
      v-else-if="threads?.length === 0 && emptyVariant === 'subtle'"
      class="px-5 text-sm text-text-secondary"
    >
      {{ $t("channel.sections.threads.announcementsEmpty") }}
    </p>

    <div
      v-else-if="threads?.length === 0"
      class="mt-4 flex items-center justify-center"
    >
      <h1 class="text-text text-3xl font-bold">
        {{ $t("channel.sections.threads.empty") }}
      </h1>
    </div>

    <div v-else class="flex flex-col gap-4">
      <ThreadCard
        v-for="thread in threads"
        :key="thread.id"
        :thread-fragment="thread"
        :authorization-rule-group-fragment="thread.authorization_rule_group"
        :is-pinned="channelStore.pinnedThreadIds.includes(thread.id)"
        @refetch="refetch"
        @card-click="onThreadCardClick"
      />
    </div>

    <div v-if="hasMoreThreads" class="flex justify-center mt-4">
      <Button
        type="button"
        variant="filled"
        class="btn btn-primary flex items-center gap-2 min-w-40"
        :disabled="loadingMore"
        @click="loadMore"
      >
        <Spinner v-if="loadingMore" class="size-4" />
        <span v-else>{{ $t("channel.leaderboard.loadMore") }}</span>
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { Icon } from "@iconify/vue";
import { useRouter } from "vue-router";
import { RouteNames } from "@/router/app.router";
import { useChannelStore } from "@/stores/channel.store";

import ThreadCard from "./thread-card.vue";
import Button from "@/components/ui/button/button.vue";
import { graphql as gql } from "@/generated";
import { useTemplateRef } from "vue";
import { useInfiniteQuery } from "@/composables/use-infinite-query";
import ThreadCardSkeleton from "@/components/ui/skeleton/thread-card-skeleton.vue";
import Spinner from "@/components/ui/spinner.vue";

const ThreadForChannelSectionDocument = gql(/* GraphQL */ `
  query ThreadForChannelSection(
    $channel_id: String!
    $community_id: String
    $page: Float
    $limit: Float
  ) {
    threadsForChannel(
      channel_id: $channel_id
      community_id: $community_id
      page: $page
      limit: $limit
    ) {
      ...ThreadlikeForCard
      id
      created_at
      authorization_rule_group {
        ...AuthorizationRuleGroupForGating
      }
      thread_communities {
        community {
          id
          name
        }
      }
    }
  }
`);

interface _ThreadForChannelVariables {
  channel_id: string;
  community_id?: string;
  page?: number;
  limit?: number;
}

withDefaults(
  defineProps<{
    /**
     * Empty-state presentation. "heading" (default) keeps the legacy large
     * centered headline; "subtle" renders a quiet inline line ("No announcements
     * yet") for the ANNOUNCEMENTS modal in the new channel layout.
     */
    emptyVariant?: "heading" | "subtle";
    /**
     * Suppress the section's own "Threads" header + create button. Used when the
     * section is hosted inside the ANNOUNCEMENTS modal, which titles the
     * surface itself. Defaults false so the inline channel-page use is unchanged.
     */
    hideHeader?: boolean;
  }>(),
  {
    emptyVariant: "heading",
    hideHeader: false,
  },
);

const channelStore = useChannelStore();
const rootElementRef = useTemplateRef("rootElement");

const {
  items: threads,
  loading: queryLoading,
  loadMore,
  refetch,
  hasMore,
  hasInitialLoad,
} = useInfiniteQuery({
  query: ThreadForChannelSectionDocument,
  variables: computed(() => ({
    channel_id: channelStore.channelSlug!,
    community_id: channelStore.selectedCommunity ?? undefined,
  })),
  limit: 10,
  pause: computed(() => !channelStore.channelSlug),
  extractData: (result) => result?.threadsForChannel,
});

const loading = computed(
  () => (queryLoading.value && !hasInitialLoad.value) || !channelStore.channel,
);
const loadingMore = computed(() => queryLoading.value && hasInitialLoad.value);

const hasMoreThreads = computed(() => hasMore.value);

const router = useRouter();

const onClick = () => {
  router.push({
    name: RouteNames.ThreadsNew,
    params: { channel: channelStore.channelSlug },
  });
};

const onThreadCardClick = (id: string) => {
  router.push({
    name: RouteNames.ThreadsItem,
    params: {
      channel: channelStore.channelSlug,
      thread_id: id,
    },
  });
};

defineExpose({
  rootElement: rootElementRef,
  isLoadingThreads: loading,
  hasThreads: threads,
});
</script>
